import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { useLocation } from "react-router"
import { Collapse, Card, Button, Flex, message } from "antd"
import { ArrowLeftOutlined, SettingOutlined, PlusOutlined, SyncOutlined, InfoCircleOutlined, RedoOutlined } from "@ant-design/icons"
import CardPrograms from "../../components/CardDetails/CardPrograms"
import GridVisualization from "../../components/GridVisualization"
import { fetchSensorMeta, fetchSensorValues, fetchCardDashboard, checkForUpdates, rebootCard, fetchCardData, getIRLogs } from "../../api/Cards"
import { POLL_INTERVAL } from "../../api/Cards/constants"
import { useUser } from "../../contexts/UserContext"
import { convertObjectKeysToCamelCase } from "../../utils/helpers"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import CardSettingsDrawer from "../../components/CardDetails/CardSettingsDrawer"
import CardInfoDrawer from "../../components/CardDetails/CardInfoDrawer"
import "../../components/CardDetails/CardPrograms.css"
import ValveControlModal from "../../components/CardDetails/Modals/manualcontrol"
import FaultCodesModal from "../../components/CardDetails/Modals/Faultcodes"
import EventsModal from "../../components/CardDetails/Modals/events"
import SensorConfig from "../../components/CardDetails/Modals/SensorConfig"
import ServiceNotes from "../../components/CardDetails/Modals/ServiceNotes"
import { EditOutlined, PlayCircleOutlined } from "../../../node_modules/@mui/icons-material/index"
import { fetchCardSettings } from "../../api/Cards"

const MAX_DATA_POINTS = 50

const CardDetails = () => {
    const location = useLocation()
    let { id } = useParams()
    const [card, setCard] = useState(null)
    const { getToken, isReadOnly, setRemoteProps } = useUser()
    const intervalRef = useRef(null)
    const counterRef = useRef(0)
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [infoVisible, setInfoVisible] = useState(false)
    const [valveControlVisible, setValveControlVisible] = useState(false)
    const [faultCodesVisible, setFaultCodesVisible] = useState(false)
    const [eventsVisible, setEventsVisible] = useState(false)
    const [notificationsShow, setNotificationsShow] = useState(false)
    const [sensorConfigVisible, setSensorConfigVisible] = useState(false)
    //const [isProgramRunning, setIsProgramRunning] = useState(false);
    const sensorId = useRef(0)
    const cardName = (card && card.cardName) ? card.cardName : "[Unnamed card]"
    const modelName = (card && card.modelName) ? card.modelName : "[Unnamed model]"
    const cardTitle = `${cardName} - ${modelName}`
    const [messageApi, contextHolder] = message.useMessage()

    id = id.replaceAll('!', '/');

    const token = useMemo(() => getToken(), [getToken])
    const showDrawer = () => setDrawerVisible(true)
    const showInfo = () => setInfoVisible(true)
    const updateFirmware = () => {
        messageApi.success(t("page.card-details.update-message"))
        checkForUpdates(token, id, card.cardIndex)
    }


    async function fetchCard() {
        //console.log({ Token: getToken(), CardID: id, CardIndex: 0 })
        const data = await fetchCardData({ Token: getToken(), CardID: id, CardIndex: 0 });
        if (typeof data === "number") {
            console.log("Disconnect: ", data)
            navigate('/dashboard');
        }

        //Needs to be mapped to match what's in AdminDashboard.js...
        setCard({
            id: data.CardId,
            cardIndex: data.CardIndex,
            cardName: data.CardName,
            modelName: data.ModelName,
            status: data.Status,
            serial: data.SerialNo,
            hwmodel: data.Hwmodel,
            hwver: data.Hwver,
            swver: data.Swver });
    }

    const rebootDevice = () => {

        async function performReboot() {
            const data = await rebootCard({ Token: token, CardID: id, CardIndex: card.cardIndex });
            messageApi.success(t('page.card-details.rebootmsg'));
            navigate(-1);
        }
        performReboot();
    }
        

    //console.log(card)

    const cardData = useRef(null)
    const [programState, setProgramState] = useState({remainingSeconds: 0, currentPosition: 0, programIndex: -1 })

    const valveControlClose = () => setValveControlVisible(false)

    const valveControlShow = () => setValveControlVisible(true)

    const faultCodesShow = () => setFaultCodesVisible(true)

    const faultCodesHide = () => setFaultCodesVisible(false)

    const eventsShow = () => setEventsVisible(true)

    const eventsHide = () => setEventsVisible(false)

    const donotificationsShow = () => setNotificationsShow(true)

    const donotificationsHide = () => setNotificationsShow(false)

    const onSensorConfig = (asensorId) => { setSensorConfigVisible(true); sensorId.current = asensorId; }
    const onSensorConfigHide = () => setSensorConfigVisible(false)



    const [graphData, setGraphData] = useState([])

    useEffect(() => {
        fetchCard()
    }, [])

    useEffect(() => {
        


        if (card == null || !token || card.status === 3) return

        
    }, [id, card, token])

    async function fetchSensorValuesCB() {
        if (!card || !cardData.current)
            return

        const collection = await fetchCardDashboard(token, id, card.cardIndex);
        if (typeof collection == "number") {
            console.log("gone offline")
            navigate(-1)
            return
        }
        const values = collection.values;
        setProgramState(collection.programState)
        const newCardData = cardData.current.map((sensor, i) => ({
            ...sensor,
            historicValues: sensor.historicValues
                ? [...sensor.historicValues, { count: counterRef.current, value: values[i] / 10 }].slice(MAX_DATA_POINTS * -1)
                : [{ count: counterRef.current, value: values[i] / 10 }],
            sensorDomain: sensor.sensorUnit.toLowerCase() === "ph" ? [0, 14] : undefined
        }))

        

        cardData.current = newCardData
        setGraphData(
            cardData.current.map(({ sensorName, sensorUnit, value, historicValues, sensorDomain }, i) => ({
                key: i,
                label: sensorName,
                extra: (values[i] / 10.0) + " " + sensorUnit,
                children: <GridVisualization id={card.id} sensor={i} navigate={navigate} key={sensorName} title={sensorName} data={historicValues} domain={sensorDomain} onSensorConfig={onSensorConfig} />
           })))
        
        counterRef.current = counterRef.current + 1

    }//, [token, id, card])

    useEffect(() => {
        if (!card) return

        async function fetchData() {
            const data = await fetchSensorMeta(token, id, card.cardIndex)
            if (typeof data == "number") {
                console.log("gone offline")
                navigate(-1)
                return
            }
            if (!data) return
            cardData.current = convertObjectKeysToCamelCase(data)
        }

        //Fetch properties prior to rendering
        async function loadSettings() {
            const data = await fetchCardSettings({ token, cardIndex: card.cardIndex, cardID: id })
            if (typeof data == "number") {
                return
            }
            setRemoteProps(data)
        }

        //fetch IR logs
        async function fetchIRData() {
            const data = await getIRLogs({ token, cardIndex: card.cardIndex, cardID: id })
            if (typeof data == "number") {
                //console.log("gone offline")
                //navigate(-1)
                return
            }
            console.log("IR Data: ", data);
        }

        loadSettings()
        fetchData()
        fetchIRData()

        intervalRef.current = setInterval(fetchSensorValuesCB, POLL_INTERVAL)
        
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [card])




    return (
        <div>
            {contextHolder}
            {card != null && (<>
                <div className="pd-details">
                    <Card className="pd-details-card">
                    {(
                        <div>
                            <Button onClick={() => navigate(-1)} type="primary" shape="circle" icon={<ArrowLeftOutlined />} size="large" />
                            <h2 className="pd-product-name">{card?.cardName ?? "[Untitled]"}</h2>
                            <div style={{margin:"0 auto", align:"center", display:"flex", width:"100%", justifyContent:"center", flexDirection: "row", flexWrap:"wrap"}}>
                                {!isReadOnly && (<Button icon={<SettingOutlined />} style={{ position: "relative", width: "25%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={showDrawer}>
                                    {t("page.card-details.settings")}
                                </Button>)}

                                <Button icon={<SyncOutlined />} style={{ position: "relative", width: "25%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={updateFirmware}>
                                    {t("page.card-details.updates")}
                                </Button>
                                <Button icon={<InfoCircleOutlined />} style={{ position: "relative", width: "25%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={showInfo}>
                                    {t("page.card-details.settings.info.title")}
                                    </Button>
                                    {!isReadOnly && (<Button icon={<PlayCircleOutlined />} disabled={programState.programIndex != -1} style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={valveControlShow}>
                                    {t("page.card-details.manualvalvecontrol")}
                                </Button>)}
                                <Button icon={<InfoCircleOutlined />} style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={faultCodesShow} >
                                    {t("page.card-details.faultcodes")}
                                </Button>
                                <Button icon={<InfoCircleOutlined />} style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={eventsShow} >
                                    {t("page.card-details.events")}
                                </Button>
                                {!isReadOnly && (<Button icon={<RedoOutlined />} style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={rebootDevice} >
                                        {t('page.card-details.reboot')}
                                    </Button>)}

                                    <Button icon={<EditOutlined />} style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} onClick={donotificationsShow}>
                                    {t('page.card-details.servicehistory')}
                                    </Button>

                                <div style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} > </div>
                                <div style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} > </div>
                                <div style={{ position: "relative", width: "24.99%", minWidth: "200px", height: "50px", verticalAlign: "top" }} > </div>
                            </div>
                        </div>
                    )}
                    </Card>
            </div>
            <div style={{marginTop: "40px" }}>
                <CardPrograms card={card} programState={programState} />
                <h3 style={{ textAlign: "center", color: "#222" }}>{cardTitle}</h3>
                    {cardData.current && cardData.current.length > 0 && <Collapse accordion={true} destroyOnHidden={true} items={graphData} style={{ width: "100%", margin: "1rem 0" }} />}
                <CardSettingsDrawer drawerVisible={drawerVisible} setDrawerVisible={setDrawerVisible} card={card} />
                <CardInfoDrawer drawerVisible={infoVisible} setDrawerVisible={setInfoVisible} hwVer={card.hwver} swVer={card.swver} cardID={card.id} />
                    {valveControlVisible && (<ValveControlModal programState={programState} metaData={{ Token: token, CardID: id, CardIndex: card.cardIndex }} onClose={valveControlClose} />)}
                    {faultCodesVisible && (<FaultCodesModal metaData={{ Token: token, CardID: id, CardIndex: card.cardIndex }} onClose={faultCodesHide} />)}
                    {eventsVisible && (<EventsModal metaData={{ Token: token, CardID: id, CardIndex: card.cardIndex }} onClose={eventsHide} />)}
                    {sensorConfigVisible && (<SensorConfig Card={{ Token: getToken(), CardID: id, CardIndex: 0 }} sensorId={sensorId.current} onClose={onSensorConfigHide}></SensorConfig>)}
                    {notificationsShow && <ServiceNotes Card={{ Token: getToken(), CardID: id, CardIndex: 0 }} onClose={donotificationsHide} />}
            </div>
             </>)}
        </div>
    )
}
export default CardDetails
